import React, { useState } from "react";
import { useParams } from "react-router-dom";
import ReactCountryFlag from "react-country-flag";
import ReactJson from "react-json-view";
import { Helmet } from "react-helmet-async";
import {
  Box,
  Typography,
  Stack,
  Tabs,
  Tab,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  LinearProgress,
} from "@mui/material";
import LayerdIcon from "../../../common/LayerdIcon";
import { DETECTION_ICONS } from "../../../../icons/detectionLucideIcons";

import { getResStatusString, isReqBlockedBySD } from "../../application/analytics/common";

import colors from "../../../../utils/colors";
import useAuth from "../../../../hooks/useAuth";
import useEvent from "../../../../hooks/user/useEvent";
import { getStatusLabel } from "./WAFEventView";
import { outlinedButtonSx } from "../../../common/Button";

const requestTitleSx =  { fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: colors.neutral[1000] };
const requestValueSx =  { fontSize: 14, lineHeight: "25px", color: colors.neutral[800] };

const RateLimitEventView = () => {
  const { eventId } = useParams();

  const { isAuthenticated } = useAuth();
  const { getRlEvent, rlEvent, setErr } = useEvent();

  const [selectedTab, setSelectedTab] = useState("timeline");

  React.useEffect(() => {
    window.scrollTo(0, 0);
    if (isAuthenticated) {
      getRlEvent(eventId);
    }
    return () => setErr(null);
  }, [isAuthenticated, setErr, getRlEvent, eventId]);

  return (
    <>
      <Helmet title="Rate Limit Event" />
      {rlEvent === null && (
        <Box sx={{ width: "100%", position: "fixed", top: 66 }}>
          <LinearProgress
            sx={{
              "& .MuiLinearProgress-bar1Indeterminate": { backgroundColor: colors.primary[200] },
              "& .MuiLinearProgress-bar2Indeterminate": { backgroundColor: colors.primary[200] },
              height: 4,
            }}
          />
        </Box>
      )}
      <Box sx={{ p: 6 }}>
        <Stack spacing={6}>
          <Stack direction={"row"} alignItems={"center"} spacing={1} onClick={() => window.history.back()} sx={{ cursor: "pointer" }}>
            <LayerdIcon icon={DETECTION_ICONS.back} size={20} color="primaryLight" />
            <Typography sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium", color: colors.primary[300] }}>
              Rate Limit Event
            </Typography>
            <Typography sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium", color: colors.neutral[800] }}>
              /
            </Typography>
            <Typography sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium", color: colors.neutral[800] }}>
              Activity log
            </Typography>
          </Stack>
          <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} spacing={3}>
            <Tabs
              value={selectedTab}
              onChange={(_, v) => setSelectedTab(v)}
              sx={{
                minHeight: "inherit",
                height: 32,
                "& .MuiTabs-flexContainer": { border: "none", px: 0, py: 0, gap: "8px", height: 32 },
                "& .MuiTabs-indicator": { height: "1px", backgroundColor: colors.secondary[300] },
                "& button": { p: 0, minWidth: "inherit", minHeight: "inherit" },
                "& button .MuiTypography-root": { color: colors.neutral[700] },
                "& .Mui-selected .MuiTypography-root": { color: colors.neutral[1000] },
              }}
            >
              <Tab
                value={"timeline"}
                label={<Typography sx={ { fontSize: 14 }}>Timeline</Typography>}
                sx={{ width: 106 }}
              />
              <Tab
                value={"rawdata"}
                label={<Typography sx={ { fontSize: 14 }}>Raw data</Typography>}
                sx={{ width: 106 }}
              />
            </Tabs>
            <Button
              startIcon={<LayerdIcon icon={DETECTION_ICONS.download} size={16} color="neutralDark" />}
              sx={{ width: 128, height: 32, ...outlinedButtonSx }}
            >
              Download
            </Button>
          </Stack>
          {selectedTab === "timeline" && (
            <Stack spacing={6}>
              <Stack direction={"row"} alignItems={"center"} spacing={6} sx={{ py: 3 }}>
                <Stack spacing={1}>
                  <Typography
                    sx={ { fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: colors.neutral[1000] }}
                  >
                    Host name
                  </Typography>
                  <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[800] }}>
                    {rlEvent?.host_name}
                  </Typography>
                </Stack>
                <Stack spacing={1}>
                  <Typography
                    sx={ { fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: colors.neutral[1000] }}
                  >
                    Source IP
                  </Typography>
                  <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[800] }}>
                    <ReactCountryFlag
                      countryCode={rlEvent?.country_iso_code}
                      svg
                      style={{
                        width: "26px",
                        height: "18px",
                      }}
                    />{" "}
                    {rlEvent?.src_ip}
                  </Typography>
                </Stack>
              </Stack>
              <Accordion
                defaultExpanded
                sx={{
                  border: `solid 1px ${colors.neutral[200]}!important`,
                  borderRadius: "8px",
                  "& .MuiAccordionSummary-content": { m: 0 },
                  "& .MuiAccordionSummary-root": { minHeight: "inherit", p: 4 },
                  "& .MuiAccordionDetails-root ": { border: "none", px: 4, pb: 4, pt: 2 },
                }}
              >
                <AccordionSummary expandIcon={<LayerdIcon icon={DETECTION_ICONS.chevronDown} size={20} color="neutral" />}>
                  <Stack direction={"row"} alignItems={"center"} spacing={2}>
                    <LayerdIcon icon={DETECTION_ICONS.request} size={20} color="primary" />
                    <Typography
                      sx={ { fontSize: 20, lineHeight: "30px", fontWeight: "medium", color: colors.neutral[1000] }}
                    >
                      Reqeust
                    </Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={6}>
                    <Stack direction={"row"} alignItems={"center"} spacing={6}>
                      <Stack spacing={1}>
                        <Typography sx={requestTitleSx}>Path</Typography>
                        <Typography sx={requestValueSx}>{rlEvent?.uri}</Typography>
                      </Stack>
                      <Stack spacing={1}>
                        <Typography sx={requestTitleSx}>Method</Typography>
                        <Typography sx={requestValueSx}>{rlEvent?.method}</Typography>
                      </Stack>
                      <Stack spacing={1}>
                        <Typography sx={requestTitleSx}>Host</Typography>
                        <Typography sx={requestValueSx}>{rlEvent?.host_name}</Typography>
                      </Stack>
                      <Stack spacing={1}>
                        <Typography sx={requestTitleSx}>Status</Typography>
                        {getStatusLabel(getResStatusString(rlEvent?.resStatus))}
                      </Stack>
                    </Stack>
                    {/* Source / identity panel — v2 rate-limit events carry these
                        directly on the row. The legacy Headers panel (UA / Referer)
                        was removed because pingora's RL writer doesn't include
                        those fields — they were always rendering as "-". */}
                    <Box>
                      <Grid container spacing={4}>
                        <Grid item xs={12}>
                          <Stack direction={"row"} alignItems={"center"} spacing={2}>
                            <LayerdIcon icon={DETECTION_ICONS.header} size={20} color="primary" />
                            <Typography
                              sx={{
                                fontSize: 16,
                                lineHeight: "24px",
                                fontWeight: "medium",
                                color: colors.neutral[1000],
                              }}
                            >
                              Source identity
                            </Typography>
                          </Stack>
                        </Grid>
                        <Grid item xs={6}>
                          <Stack spacing={1}>
                            <Typography sx={{ ...requestTitleSx, wordBreak: "break-all", "text-transform": "capitalize" }}>Source IP</Typography>
                            <Typography sx={requestValueSx}>{rlEvent?.src_ip ?? "—"}</Typography>
                          </Stack>
                        </Grid>
                        <Grid item xs={6}>
                          <Stack spacing={1}>
                            <Typography sx={{ ...requestTitleSx, wordBreak: "break-all", "text-transform": "capitalize" }}>ASN</Typography>
                            <Typography sx={requestValueSx}>{rlEvent?.asn ?? "—"}</Typography>
                          </Stack>
                        </Grid>
                        <Grid item xs={6}>
                          <Stack spacing={1}>
                            <Typography sx={{ ...requestTitleSx, wordBreak: "break-all", "text-transform": "capitalize" }}>UID</Typography>
                            <Typography sx={{ ...requestValueSx, fontFamily: "ui-monospace, monospace", fontSize: 13 }}>{rlEvent?.uid ?? "—"}</Typography>
                          </Stack>
                        </Grid>
                        <Grid item xs={6}>
                          <Stack spacing={1}>
                            <Typography sx={{ ...requestTitleSx, wordBreak: "break-all", "text-transform": "capitalize" }}>Composite Fingerprint</Typography>
                            <Typography sx={{ ...requestValueSx, fontFamily: "ui-monospace, monospace", fontSize: 13 }}>{rlEvent?.fp ?? "—"}</Typography>
                          </Stack>
                        </Grid>
                        <Grid item xs={6}>
                          <Stack spacing={1}>
                            <Typography sx={{ ...requestTitleSx, wordBreak: "break-all", "text-transform": "capitalize" }}>Country</Typography>
                            <Typography sx={requestValueSx}>{rlEvent?.country_iso_code ?? "—"}</Typography>
                          </Stack>
                        </Grid>
                        <Grid item xs={12}>
                          <Stack spacing={1}>
                            <Typography sx={{ ...requestTitleSx, wordBreak: "break-all", "text-transform": "capitalize" }}>JA3 Fingerprint</Typography>
                            <Typography sx={{ ...requestValueSx, fontFamily: "ui-monospace, monospace", fontSize: 13, wordBreak: "break-all" }}>
                              {rlEvent?.ja3 || rlEvent?.ja3_hash || "—"}
                            </Typography>
                          </Stack>
                        </Grid>
                        <Grid item xs={6}>
                          <Stack spacing={1}>
                            <Typography sx={{ ...requestTitleSx, wordBreak: "break-all", "text-transform": "capitalize" }}>Host</Typography>
                            <Typography sx={requestValueSx}>{rlEvent?.host_name ?? "—"}</Typography>
                          </Stack>
                        </Grid>
                      </Grid>
                    </Box>
                  </Stack>
                </AccordionDetails>
              </Accordion>
              <Accordion
                defaultExpanded
                sx={{
                  border: `solid 1px ${colors.neutral[200]}!important`,
                  borderRadius: "8px",
                  "& .MuiAccordionSummary-content": { m: 0 },
                  "& .MuiAccordionSummary-root": { minHeight: "inherit", p: 4 },
                  "& .MuiAccordionDetails-root ": { border: "none", px: 4, pb: 4, pt: 2 },
                }}
              >
                <AccordionSummary expandIcon={<LayerdIcon icon={DETECTION_ICONS.chevronDown} size={20} color="neutral" />}>
                  <Stack direction={"row"} alignItems={"center"} spacing={2}>
                    <LayerdIcon icon={DETECTION_ICONS.wafDetection} size={20} color="primary" />
                    <Typography
                      sx={ { fontSize: 20, lineHeight: "30px", fontWeight: "medium", color: colors.neutral[1000] }}
                    >
                      Rate Limiting
                    </Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={6}>
                    <Stack direction={"row"} alignItems={"center"} spacing={4}>
                      <Stack spacing={2}>
                        <Stack direction={"row"} alignItems={"center"} spacing={1}>
                          <LayerdIcon icon={DETECTION_ICONS.requestBlocked} size={20} color="primary" />
                          <Typography
                            sx={{
                              fontSize: 14,
                              lineHeight: "21px",
                              fontWeight: "medium",
                              color: colors.neutral[1000],
                            }}
                          >
                            Request blocked
                          </Typography>
                        </Stack>
                        <Typography sx={ { fontSize: 14, lineHeight: "21px", color: colors.neutral[800], pl: 7 }}>
                          {rlEvent?.blocked_bool === true || rlEvent?.sd_rate_limited === 1 || isReqBlockedBySD(rlEvent?.resStatus) ? "True" : "False"}
                        </Typography>
                      </Stack>
                    </Stack>
                    {/* v2 rate-limit fields — read from the top-level event shape
                        produced by parseV2RlEventRow(). The legacy
                        sd_rate_limit_rule.* paths were the pre-v2 ES schema and
                        rendered "-" for every field on every modern event. */}
                    <Box sx={{ width: "100%" }}>
                      <Grid container spacing={4}>
                        <Grid item xs={6}>
                          <Stack spacing={1}>
                            <Typography sx={{ ...requestTitleSx, wordBreak: "break-all", "text-transform": "capitalize" }}>Rule Name</Typography>
                            <Typography sx={requestValueSx}>{rlEvent?.rule_name ?? "—"}</Typography>
                          </Stack>
                        </Grid>
                        <Grid item xs={6}>
                          <Stack spacing={1}>
                            <Typography sx={{ ...requestTitleSx, wordBreak: "break-all", "text-transform": "capitalize" }}>Rule ID</Typography>
                            <Typography sx={{ ...requestValueSx, fontFamily: "ui-monospace, monospace", fontSize: 13 }}>{rlEvent?.rule_id ?? "—"}</Typography>
                          </Stack>
                        </Grid>
                        <Grid item xs={6}>
                          <Stack spacing={1}>
                            <Typography sx={{ ...requestTitleSx, wordBreak: "break-all", "text-transform": "capitalize" }}>Mode</Typography>
                            <Typography sx={requestValueSx}>{rlEvent?.mode ?? "—"}</Typography>
                          </Stack>
                        </Grid>
                        <Grid item xs={6}>
                          <Stack spacing={1}>
                            <Typography sx={{ ...requestTitleSx, wordBreak: "break-all", "text-transform": "capitalize" }}>Action</Typography>
                            <Typography sx={requestValueSx}>{rlEvent?.action ?? "—"}</Typography>
                          </Stack>
                        </Grid>
                        <Grid item xs={6}>
                          <Stack spacing={1}>
                            <Typography sx={{ ...requestTitleSx, wordBreak: "break-all", "text-transform": "capitalize" }}>Verdict</Typography>
                            <Typography sx={requestValueSx}>{rlEvent?.verdict ?? "—"}</Typography>
                          </Stack>
                        </Grid>
                        <Grid item xs={6}>
                          <Stack spacing={1}>
                            <Typography sx={{ ...requestTitleSx, wordBreak: "break-all", "text-transform": "capitalize" }}>Threshold</Typography>
                            <Typography sx={requestValueSx}>
                              {(rlEvent?.limit != null && rlEvent?.period_s != null) ? `${rlEvent.limit} request${rlEvent.limit === 1 ? "" : "s"} / ${rlEvent.period_s}s` : "—"}
                            </Typography>
                          </Stack>
                        </Grid>
                        <Grid item xs={12}>
                          <Stack spacing={1}>
                            <Typography sx={{ ...requestTitleSx, wordBreak: "break-all", "text-transform": "capitalize" }}>Bucket Key</Typography>
                            <Typography sx={{ ...requestValueSx, fontFamily: "ui-monospace, monospace", fontSize: 13 }}>{rlEvent?.bucket_key ?? "—"}</Typography>
                          </Stack>
                        </Grid>
                      </Grid>
                    </Box>
                  </Stack>
                </AccordionDetails>
              </Accordion>
              <Accordion
                defaultExpanded
                sx={{
                  border: `solid 1px ${colors.neutral[200]}!important`,
                  borderRadius: "8px",
                  "& .MuiAccordionSummary-content": { m: 0 },
                  "& .MuiAccordionSummary-root": { minHeight: "inherit", p: 4 },
                  "& .MuiAccordionDetails-root ": { border: "none", px: 4, pb: 4, pt: 2 },
                }}
              >
                <AccordionSummary expandIcon={<LayerdIcon icon={DETECTION_ICONS.chevronDown} size={20} color="neutral" />}>
                  <Stack direction={"row"} alignItems={"center"} spacing={2}>
                    <LayerdIcon icon={DETECTION_ICONS.response} size={20} color="primary" />
                    <Typography
                      sx={ { fontSize: 20, lineHeight: "30px", fontWeight: "medium", color: colors.neutral[1000] }}
                    >
                      Response
                    </Typography>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Stack spacing={6}>
                    <Box>
                      <Stack direction={"row"} spacing={6}>
                        <Stack
                          spacing={2}
                          sx={{
                            border: `solid 1px ${isReqBlockedBySD(rlEvent?.resStatus) ? colors.red[100] : colors.primary[300]}`,
                            backgroundColor: isReqBlockedBySD(rlEvent?.resStatus) ? colors.red["10"] : colors.primary["alpha"],
                            borderRadius: "8px",
                            px: 4,
                            py: 2,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: 14,
                              lineHeight: "21px",
                              fontWeight: "medium",
                              color: colors.neutral[1000],
                            }}
                          >
                            Blocked By Layerd AI
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 14,
                              lineHeight: "21px",
                              color: isReqBlockedBySD(rlEvent?.resStatus) ? colors.red[100] : colors.primary[300],
                            }}
                          >
                            {isReqBlockedBySD(rlEvent?.resStatus) ? "True" : "False"}
                          </Typography>
                        </Stack>
                        <Stack spacing={2} sx={{ px: 4, py: 2 }}>
                          <Stack direction={"row"} alignItems={"center"} spacing={1}>
                            <LayerdIcon icon={DETECTION_ICONS.responseStatus} size={20} color="primary" />
                            <Typography
                              sx={{
                                fontSize: 14,
                                lineHeight: "21px",
                                fontWeight: "medium",
                                color: colors.neutral[1000],
                              }}
                            >
                              Response
                            </Typography>
                          </Stack>
                          <Typography
                            sx={{
                              fontSize: 14,
                              lineHeight: "21px",
                              color: colors.primary[300],
                              pl: 7,
                            }}
                          >
                            {rlEvent?.resStatus}
                          </Typography>
                        </Stack>
                      </Stack>
                    </Box>
                  </Stack>
                </AccordionDetails>
              </Accordion>
            </Stack>
          )}
          {selectedTab === "rawdata" && (
            <Box>
              <ReactJson
                name="raw"
                src={rlEvent?.raw}
                displayDataTypes={false}
                style={{ wordBreak: "break-word", fontSize: 13 }}
              />
            </Box>
          )}
        </Stack>
      </Box>
    </>
  );
};
export default RateLimitEventView;
